/**
 * Settings > Billing: one card per school with its plan, add-on and seat
 * summary, and the plan / seats / requests / invite / Stripe / activation
 * actions. The focused school (opened from the Schools tab) is highlighted.
 */
import React from 'react';
import { View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Text } from '@/components/ui/text';
import { ADMIN_COLORS } from '@/components/admin/AdminUI';
import { useTranslation } from '../../../../hooks/useTranslation';
import { formatCurrency, getBillingSeed } from '../platformAdminFormat';
import type { School } from '../platformAdminTypes';
import type { SchoolActions } from '../hooks/useSchoolActions';

interface SchoolBillingListProps {
  schools: School[];
  focusedSchoolId: number | null;
  schoolActions: SchoolActions;
  onCreateSchool: () => void;
}

export default function SchoolBillingList({
  schools,
  focusedSchoolId,
  schoolActions,
  onCreateSchool,
}: SchoolBillingListProps) {
  const { t } = useTranslation();
  const {
    setShowPlanModal,
    setShowSeatModal,
    setShowPlanChangeRequests,
    setShowBillModal,
    syncStripe,
    toggleSubscription,
  } = schoolActions;

  if (!schools.length) {
    return (
      <Card>
        <CardContent className="p-8 items-center">
          <MaterialIcons name="price-change" size={42} color="#6b7280" />
          <Text className="text-lg font-medium text-foreground mt-4 mb-2">
            {t('platformAdmin.noSchools')}
          </Text>
          <Text className="text-base text-muted-foreground text-center mb-4">
            {t('platformAdmin.createFirstSchool')}
          </Text>
          <Button variant="default" onPress={onCreateSchool}>
            <Text className="text-sm text-white font-semibold">
              {t('platformAdmin.createSchool')}
            </Text>
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <View className="gap-4">
      {schools.map((school) => {
        const planConfigured = Boolean(
          school.billing_currency && school.billing_base_price_cents && school.billing_base_seats
        );
        const basePrice = formatCurrency(school.billing_base_price_cents, school.billing_currency);
        const addonPrice = formatCurrency(school.billing_addon_price_cents, school.billing_currency);
        const seed = getBillingSeed(school);
        const isFocused = focusedSchoolId === school.id;

        return (
          <Card
            key={school.id}
            nativeID={`platform-admin-settings-card-${school.id}`}
            className={`${isFocused ? 'border-2 border-primary/60 shadow-lg shadow-primary/20' : ''} transition-all duration-200`}
          >
            <CardHeader className="pb-2">
              <View className="flex-row justify-between items-start">
                <View className="flex-1">
                  <Text className="text-lg font-semibold text-foreground">{school.name}</Text>
                  <Text className="text-xs text-muted-foreground">@{school.slug}</Text>
                  {isFocused ? (
                    <View className="flex-row items-center mt-2">
                      <MaterialIcons name="radio-button-checked" size={16} color={ADMIN_COLORS.primary} />
                      <Text className="text-xs font-semibold text-primary ml-1">
                        {t('platformAdmin.focusedSchool')}
                      </Text>
                    </View>
                  ) : null}
                </View>
                <View
                  className={`px-3 py-1 rounded-full ${school.subscription_status === 'active' ? 'bg-green-100' : 'bg-red-100'
                    }`}
                >
                  <Text
                    className={`text-xs font-semibold ${school.subscription_status === 'active' ? 'text-green-700' : 'text-red-700'
                      }`}
                  >
                    {(school.subscription_status || t('platformAdmin.unknownStatus')).toUpperCase()}
                  </Text>
                </View>
              </View>
            </CardHeader>
            <CardContent className="gap-3">
              {planConfigured ? (
                <View className="gap-2">
                  <View className="flex-row items-center">
                    <MaterialIcons name="price-change" size={16} color="#6b7280" />
                    <Text className="text-sm text-muted-foreground ml-2 flex-1">
                      {t('platformAdmin.planSummary', {
                        price: basePrice,
                        seats: school.billing_base_seats,
                      })}
                    </Text>
                  </View>
                  {school.billing_addon_package_size && addonPrice ? (
                    <View className="flex-row items-center">
                      <MaterialIcons name="stacked-line-chart" size={16} color="#6b7280" />
                      <Text className="text-sm text-muted-foreground ml-2 flex-1">
                        {t('platformAdmin.addonSummary', {
                          price: addonPrice,
                          seats: school.billing_addon_package_size,
                        })}
                      </Text>
                    </View>
                  ) : null}
                  <View className="flex-row items-center">
                    <MaterialIcons name="groups" size={16} color="#6b7280" />
                    <Text className="text-sm text-muted-foreground ml-2 flex-1">
                      {t('platformAdmin.seatCapacitySummary', {
                        current: school.current_students + (school.current_mentors || 0),
                        max: school.max_students,
                      })}
                    </Text>
                  </View>
                </View>
              ) : (
                <View className="flex-row items-center">
                  <MaterialIcons name="info" size={16} color="#f59e0b" />
                  <Text className="text-sm text-muted-foreground ml-2 flex-1">
                    {t('platformAdmin.noBillingConfigured')}
                  </Text>
                </View>
              )}

              <View className="flex-row flex-wrap gap-2 mt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() =>
                    setShowPlanModal({
                      visible: true,
                      schoolId: school.id,
                      seed,
                    })
                  }
                >
                  <Text className="text-xs font-semibold text-foreground">
                    {t('platformAdmin.editPlan')}
                  </Text>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => setShowSeatModal({ visible: true, schoolId: school.id, current: school.max_students })}
                >
                  <Text className="text-xs font-semibold text-foreground">
                    {t('platformAdmin.updateSeats')}
                  </Text>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => setShowPlanChangeRequests({ visible: true, schoolId: school.id })}
                >
                  <Text className="text-xs font-semibold text-foreground">
                    {t('platformAdmin.seatRequests')}
                  </Text>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() =>
                    setShowBillModal({
                      visible: true,
                      schoolId: school.id,
                      seed,
                    })
                  }
                >
                  <Text className="text-xs font-semibold text-foreground">
                    {t('platformAdmin.billAndInvite')}
                  </Text>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => syncStripe(school)}
                >
                  <Text className="text-xs font-semibold text-foreground">
                    {t('platformAdmin.syncStripe')}
                  </Text>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onPress={() => toggleSubscription(school)}
                >
                  <Text className="text-xs font-semibold text-foreground">
                    {school.subscription_status === 'active'
                      ? t('platformAdmin.deactivateSchool')
                      : t('platformAdmin.activateSchool')}
                  </Text>
                </Button>
              </View>
            </CardContent>
          </Card>
        );
      })}
    </View>
  );
}
